import { evaluateFormCondition } from '@ayumia/component-runtime';
import { useId, useState, type ReactNode } from 'react';
import { Icon } from '../../../design-system/icons';
import { ConditionBuilder } from '../../editor/ConditionBuilder';
import { comparisonToExpression, fromExpression, operatorsFor, type Comparison, type ComparisonOperator, type ScopeVariable } from '../../editor/condition-model';
import { InfoTip } from '../../editor/StudioPanels';
import { answersOf } from '../forms-editors';

/**
 * Contrôles visuels de l'inspecteur v2 (planches « EditeurV2-Contenu », « -Controles ») : sections repliables
 * à en-tête clair, choix segmentés, tuiles à icône, interrupteurs en bascule, pas-à-pas, et la règle en phrase
 * « Toujours ▾ / Seulement si… / Jamais » avec sa condition « Si [champ] [est] [valeur] ».
 */

export const evaluate = (expression: unknown, values: Readonly<Record<string, unknown>>): boolean => evaluateFormCondition(expression, answersOf(values));

/** Section repliable : un titre simple, un résumé facultatif à droite, ouverte par défaut. */
export function Fold({ title, summary, open = true, children, badge }: { title: string; summary?: ReactNode; open?: boolean; children: ReactNode; badge?: ReactNode }) {
  return (
    <details className="ay-v2-fold" open={open}>
      <summary>
        <Icon name="chevron-down" size={14} />
        <span className="ay-v2-fold__title">{title}</span>
        {badge}
        {summary !== undefined && <span className="ay-v2-fold__summary">{summary}</span>}
      </summary>
      <div className="ay-v2-fold__body">{children}</div>
    </details>
  );
}

/**
 * Bloc d'un groupe de l'inspecteur (« Widget », « Lecture ») : jamais repliable — le groupe l'est déjà. Sans
 * titre, il ne garde que sa pastille « modifié ici » éventuelle.
 */
export function Sub({ title, badge, children }: { title?: string; badge?: ReactNode; children: ReactNode }) {
  return (
    <div className="ay-v2-sub">
      {(title !== undefined || (badge !== undefined && badge !== null)) && (
        <span className="ay-v2-sub__title">
          {title !== undefined && <span>{title}</span>}
          {badge}
        </span>
      )}
      {children}
    </div>
  );
}

/** Ligne libellé + contrôle, l'aide en infobulle ⓘ, une pastille « modifié ici » éventuelle. */
export function Line({ label, help, children, mark, stacked = false }: { label: string; help?: string; children: ReactNode; mark?: ReactNode; stacked?: boolean }) {
  return (
    <div className={`ay-v2-line${stacked ? ' is-stacked' : ''}`}>
      <span className="ay-v2-line__label">
        {label}
        {help !== undefined && <InfoTip label={`Aide : ${label}`}>{help}</InfoTip>}
        {mark}
      </span>
      <span className="ay-v2-line__control">{children}</span>
    </div>
  );
}

/** Choix segmenté compact. */
export function Choice<T extends string | number>({ label, value, options, onChange, help, mark }: { label: string; value: T | undefined; options: ReadonlyArray<{ value: T; label: string; icon?: string; hint?: string }>; onChange: (value: T) => void; help?: string; mark?: ReactNode }) {
  return (
    <Line label={label} {...(help === undefined ? {} : { help })} mark={mark} stacked={options.length > 3 || options.reduce((total, option) => total + option.label.length, 0) > 22}>
      <span className="ay-studio__seg ay-v2-seg" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button key={String(option.value)} type="button" role="radio" aria-checked={value === option.value} onClick={() => onChange(option.value)} title={option.hint ?? option.label} aria-label={option.icon !== undefined && option.label === '' ? option.hint : undefined}>
            {option.icon !== undefined && <Icon name={option.icon} size={14} />}
            {option.label}
          </button>
        ))}
      </span>
    </Line>
  );
}

/** Tuiles à icône (choix du widget, du visuel) : grandes cibles, icône et nom. */
export function IconTiles<T extends string>({ label, value, options, onChange }: { label: string; value: T | undefined; options: ReadonlyArray<{ value: T; label: string; icon: string; hint?: string }>; onChange: (value: T) => void }) {
  return (
    <div className="ay-v2-tiles" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" role="radio" aria-checked={value === option.value} onClick={() => onChange(option.value)} title={option.hint ?? option.label} aria-label={option.hint ?? option.label}>
          <Icon name={option.icon} size={18} />
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Interrupteur en bascule (jamais une case à cocher dans l'inspecteur v2). */
export function Switch({ label, checked, onChange, help, mark }: { label: string; checked: boolean; onChange: (checked: boolean) => void; help?: string; mark?: ReactNode }) {
  return (
    <Line label={label} {...(help === undefined ? {} : { help })} mark={mark}>
      <button type="button" role="switch" aria-checked={checked} aria-label={label} className="ay-v2-switch" onClick={() => onChange(!checked)}>
        <span aria-hidden="true" />
      </button>
    </Line>
  );
}

/** Pas-à-pas − / + avec saisie, bornes facultatives. */
export function Stepper({ label, value, onChange, step = 1, min, max, unit, placeholder }: { label: string; value: number | undefined; onChange: (value: number | undefined) => void; step?: number; min?: number; max?: number; unit?: string; placeholder?: string }) {
  const clamp = (next: number): number => Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? Number.NEGATIVE_INFINITY, Math.round(next * 1000) / 1000));
  return (
    <span className="ay-v2-stepper" role="group" aria-label={label}>
      <button type="button" aria-label={`${label} : moins`} onClick={() => onChange(clamp((value ?? 0) - step))}>−</button>
      <input type="number" inputMode="decimal" aria-label={label} value={value ?? ''} placeholder={placeholder} step={step} onChange={(event) => onChange(event.target.value === '' ? undefined : clamp(Number(event.target.value)))} />
      <button type="button" aria-label={`${label} : plus`} onClick={() => onChange(clamp((value ?? 0) + step))}>+</button>
      {unit !== undefined && unit !== '' && <span className="ay-studio__muted">{unit}</span>}
    </span>
  );
}

/** Pastille « modifié ici · revenir » d'un réglage surchargé à l'emplacement (préréglage). */
export function OverrideMark({ onRevert }: { onRevert?: (() => void) | undefined }) {
  if (onRevert === undefined) return null;
  return (
    <span className="ay-v2-override">
      modifié ici ·
      <button type="button" className="ay-v2-link" onClick={onRevert}>revenir</button>
    </span>
  );
}

const SHORT: Readonly<Record<ComparisonOperator, string>> = { eq: 'est', ne: 'n’est pas', gt: 'est plus grand que', gte: 'est au moins', lt: 'est plus petit que', lte: 'est au plus', in: 'est l’un de', nin: 'n’est aucun de', filled: 'est rempli', empty: 'est vide' };

/** Condition « Si [champ] [est] [valeur] » ; une condition plus riche passe au constructeur à groupes. */
export function SimpleCondition({ value, variables, onChange, label = 'Condition' }: { value: unknown; variables: readonly ScopeVariable[]; onChange: (value: unknown) => void; label?: string }) {
  const id = useId();
  const guided = fromExpression(value);
  const [advanced, setAdvanced] = useState(guided === null || guided.items.length > 1);
  if (advanced || guided === null || guided.items.length > 1) {
    return (
      <div className="ay-v2-condition is-advanced">
        <ConditionBuilder label={label} value={value} variables={variables} evaluate={evaluate} onChange={onChange} />
        {(guided === null || guided.items.length <= 1) && <button type="button" className="ay-v2-link" onClick={() => setAdvanced(false)}>Condition simple</button>}
      </div>
    );
  }
  const first = variables[0];
  const item: Comparison = guided.items[0] ?? { ref: first?.ref ?? '', op: 'eq', ...(first?.options?.[0] === undefined ? {} : { value: first.options[0].value }) };
  const variable = variables.find((candidate) => candidate.ref === item.ref);
  const write = (next: Comparison): void => onChange(next.ref === '' ? undefined : comparisonToExpression(next));
  if (variables.length === 0) return <p className="ay-studio-empty">Aucun champ à comparer.</p>;
  return (
    <span className="ay-v2-condition">
      <span className="ay-v2-word">Si</span>
      <select aria-label={`${label} : champ`} id={id} value={item.ref} onChange={(event) => {
        const next = variables.find((candidate) => candidate.ref === event.target.value);
        write({ ref: event.target.value, op: operatorsFor(next?.type ?? 'other')[0] ?? 'eq', ...(next?.options?.[0] === undefined ? (next?.type === 'boolean' ? { value: true } : {}) : { value: next.options[0].value }) });
      }}>
        {variable === undefined && <option value={item.ref}>Champ qui n’existe plus</option>}
        {variables.map((candidate) => <option key={candidate.ref} value={candidate.ref}>{candidate.label}</option>)}
      </select>
      <select aria-label={`${label} : comparaison`} value={item.op} onChange={(event) => write({ ...item, op: event.target.value as ComparisonOperator })}>
        {operatorsFor(variable?.type ?? 'other').filter((op) => op !== 'in' && op !== 'nin').map((op) => <option key={op} value={op}>{SHORT[op]}</option>)}
      </select>
      {item.op !== 'filled' && item.op !== 'empty' && <ValueInput variable={variable} value={item.value} label={`${label} : valeur`} onChange={(next) => write({ ...item, value: next })} />}
      <button type="button" className="ay-v2-link" onClick={() => setAdvanced(true)} title="Plusieurs conditions, groupes">Condition</button>
    </span>
  );
}

/** Valeur d'une comparaison : liste pour une énumération ou un oui / non, sinon saisie. */
export function ValueInput({ variable, value, label, onChange }: { variable: ScopeVariable | undefined; value: unknown; label: string; onChange: (value: unknown) => void }) {
  if (variable?.type === 'boolean') {
    return (
      <select aria-label={label} value={value === false ? 'false' : 'true'} onChange={(event) => onChange(event.target.value === 'true')}>
        <option value="true">Oui</option>
        <option value="false">Non</option>
      </select>
    );
  }
  if (variable?.options !== undefined) {
    return (
      <select aria-label={label} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)}>
        {variable.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  const numeric = variable?.type === 'number';
  return <input aria-label={label} type={numeric ? 'number' : variable?.type === 'date' ? 'date' : 'text'} value={value === undefined || value === null ? '' : String(value)} onChange={(event) => onChange(numeric ? (event.target.value === '' ? undefined : Number(event.target.value)) : event.target.value)} />;
}

export type RuleMode = 'always' | 'when' | 'never';

/**
 * Règle en phrase (« Visible : Toujours ▾ ») : Toujours, Seulement si… (avec sa condition), Jamais. L'ordre des
 * choix suit la règle (« Lecture seule » commence par Jamais).
 */
export function Rule({ label, mode, condition, variables, onChange, order = ['always', 'when', 'never'], labels, help, mark }: {
  label: string;
  mode: RuleMode;
  condition: unknown;
  variables: readonly ScopeVariable[];
  onChange: (mode: RuleMode, condition: unknown) => void;
  order?: readonly RuleMode[];
  labels?: Partial<Record<RuleMode, string>>;
  help?: string;
  mark?: ReactNode;
}) {
  const id = useId();
  const names: Record<RuleMode, string> = { always: 'Toujours', when: 'Seulement si…', never: 'Jamais', ...labels };
  const first = variables[0];
  const starter = first === undefined ? undefined : comparisonToExpression({ ref: first.ref, op: first.options === undefined ? (first.type === 'boolean' ? 'eq' : 'filled') : 'eq', ...(first.options?.[0] === undefined ? (first.type === 'boolean' ? { value: true } : {}) : { value: first.options[0].value }) });
  return (
    <div className={`ay-v2-rule is-${mode}`}>
      <Line label={label} {...(help === undefined ? {} : { help })} mark={mark}>
        <select id={id} aria-label={label} value={mode} onChange={(event) => {
          const next = event.target.value as RuleMode;
          onChange(next, next === 'when' ? (condition ?? starter) : undefined);
        }}>
          {order.map((value) => <option key={value} value={value}>{names[value]}</option>)}
        </select>
      </Line>
      {mode === 'when' && <SimpleCondition value={condition} variables={variables} label={label} onChange={(next) => onChange('when', next)} />}
    </div>
  );
}

/** Bouton d'ajout discret d'une liste (« Ajouter un choix », « Ajouter une action »). */
export function AddButton({ label, onClick, disabled, title }: { label: string; onClick: () => void; disabled?: boolean; title?: string }) {
  return (
    <button type="button" className="ay-v2-add" onClick={onClick} disabled={disabled} title={title}>
      <Icon name="plus" size={14} />
      {label}
    </button>
  );
}
